import { useEffect, useState } from 'react';
import { MapPin, Battery, BatteryLow, Wifi, WifiOff, CheckCircle2, XCircle, RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Pre-shift health check — runs three auto-detected diagnostics before
 * the worker is allowed to clock in:
 *   1. Location permission granted (HARD requirement)
 *   2. Battery > 15% (soft warning)
 *   3. Online (soft warning)
 *
 * Props:
 *   - enabled: boolean — show the panel at all (false if org has GPS disabled)
 *   - onReady: (ready: boolean, firstPosition: GeolocationPosition | null) => void
 *
 * Emits ready=true once location is granted. firstPosition is the cached
 * first GeolocationPosition so the parent can fire an immediate ping.
 */
export function PreShiftHealthCheck({ enabled = true, onReady }) {
  const [locStatus, setLocStatus] = useState('checking');  // checking | granted | denied | unsupported | prompt
  const [firstPosition, setFirstPosition] = useState(null);
  const [batteryPct, setBatteryPct] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const runLocationCheck = async () => {
    if (!('geolocation' in navigator)) {
      setLocStatus('unsupported');
      return;
    }
    setRequesting(true);
    setLocStatus('checking');
    // Use Permissions API first if available (avoids unnecessary GPS lookups)
    if (navigator.permissions?.query) {
      try {
        const perm = await navigator.permissions.query({ name: 'geolocation' });
        if (perm.state === 'denied') {
          setLocStatus('denied');
          setRequesting(false);
          return;
        }
      } catch (_) { /* permissions API not supported - fall through to getCurrentPosition */ }
    }
    // Trigger a real GPS lookup (this also fires the browser prompt if needed)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFirstPosition(pos);
        setLocStatus('granted');
        setRequesting(false);
      },
      (err) => {
        setLocStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
        setRequesting(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Battery API (only available on Chromium browsers)
  useEffect(() => {
    if (!enabled) return;
    if (navigator.getBattery) {
      navigator.getBattery().then((bat) => {
        setBatteryPct(Math.round(bat.level * 100));
      }).catch(() => setBatteryPct(null));
    }
  }, [enabled]);

  // Online status
  useEffect(() => {
    if (!enabled) return;
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [enabled]);

  // Trigger initial location check on mount
  useEffect(() => {
    if (enabled) runLocationCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Inform parent when ready / not ready
  useEffect(() => {
    onReady?.(locStatus === 'granted', firstPosition);
  }, [locStatus, firstPosition, onReady]);

  if (!enabled) return null;

  const Row = ({ icon: Icon, label, value, ok, warn, tone }) => (
    <div className={`flex items-center gap-2 text-sm ${tone}`}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
         : warn ? <XCircle className="h-4 w-4 shrink-0 text-amber-500" />
                : <XCircle className="h-4 w-4 shrink-0 text-red-500" />}
      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
      <span className="font-medium">{label}</span>
      <span className="text-slate-500 ml-auto">{value}</span>
    </div>
  );

  const locOk = locStatus === 'granted';
  const batteryWarn = batteryPct != null && batteryPct <= 15;
  const onlineOk = online;

  return (
    <div
      className={`border rounded-lg p-3 space-y-2 ${locOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}
      data-testid="pre-shift-health-check"
    >
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-slate-900">Pre-shift checks</h4>
        {!locOk && (
          <button
            onClick={runLocationCheck}
            disabled={requesting}
            className="text-xs text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="pre-shift-recheck"
          >
            <RefreshCcw className={`h-3 w-3 ${requesting ? 'animate-spin' : ''}`} />
            {requesting ? 'Checking…' : 'Re-check'}
          </button>
        )}
      </div>

      <Row
        icon={MapPin}
        label="Location"
        value={
          locStatus === 'granted' ? 'Sharing'
          : locStatus === 'denied' ? 'Permission denied'
          : locStatus === 'unsupported' ? 'Not supported on this device'
          : locStatus === 'checking' ? 'Asking your browser…'
          : 'Unavailable'
        }
        ok={locOk}
        tone={locOk ? 'text-emerald-900' : 'text-red-700'}
      />

      <Row
        icon={batteryWarn ? BatteryLow : Battery}
        label="Battery"
        value={batteryPct == null ? 'Unknown' : `${batteryPct}%`}
        ok={batteryPct == null || !batteryWarn}
        warn={batteryWarn}
        tone={batteryWarn ? 'text-amber-700' : 'text-slate-700'}
      />

      <Row
        icon={onlineOk ? Wifi : WifiOff}
        label="Internet"
        value={onlineOk ? 'Online' : 'Offline — pings will queue'}
        ok={onlineOk}
        warn={!onlineOk}
        tone={onlineOk ? 'text-slate-700' : 'text-amber-700'}
      />

      {locStatus === 'denied' && (
        <div className="mt-2" data-testid="pre-shift-help">
          <button
            onClick={() => setHelpOpen(o => !o)}
            className="text-xs text-teal-700 hover:text-teal-900 inline-flex items-center gap-1"
          >
            {helpOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            How to turn Location on
          </button>
          {helpOpen && (
            <div className="text-xs text-slate-700 mt-2 space-y-1.5 p-2 bg-white rounded border border-slate-200">
              <p><strong>iPhone (Safari):</strong> Settings → Safari → Location → Allow. Then refresh this page.</p>
              <p><strong>Android (Chrome):</strong> Tap the 🔒 lock icon next to the URL → Permissions → Location → Allow.</p>
              <p><strong>Desktop (Chrome/Edge):</strong> Click the 🔒 next to the URL → Site settings → Location → Allow.</p>
              <p className="text-slate-500 italic">After allowing, tap "Re-check" above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
